import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { FeatureFlags } from '../../../common/ConstantsFeatureFlags';
import { getIntValue } from '../../../common/Utils/getIntValue';
import { CountySearchContext } from '../../../context/CountySearchContext';
import { GeospatialModal } from '../../../frontends/GuestExperience/selectLocation/GeospatialModal';
import { GuestLocation } from '../../../frontends/GuestExperience/selectLocation/GuestLocation.utils';
import { PostalLocationData } from '../../../frontends/GuestExperience/selectLocation/GuestLocationInput';
import { getTelemetryCoverageType } from '../../../frontends/ProviderSearch/routes/MapView/utils';
import { useAutoCompleteResults } from '../../../hooks/useAutoCompleteQuery';
import { useAutoCompleteTelemetry } from '../../../hooks/useAutoCompleteTelemetry';
import { useEventCapture } from '../../../hooks/useEventCapture';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGetPostalPoint } from '../../../hooks/useGetPostalPoint/useGetPostalPoint';
import { useLagoon } from '../../../hooks/useLagoon';
import { useLocation } from '../../../hooks/useLocation';
import { getSearchInfo } from '../../../hooks/useTypeAheadData/utils';
import { useAnalyticsStore } from '../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../../store/useCareTeamStore';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../store/useDetailsStore';
import { DetailsStore } from '../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../store/usePCPStore';
import { PCPSearchStore } from '../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../store/useSearchStore';
import { SearchState } from '../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import {
  getCoverageTypes,
  getCurrentMember,
  getProviderType,
} from '../../../utils/user.utils';
import { adobeImpressionTrackEvent } from '../../AdobeTagging/adobeImpressionTrackEvent';
import {
  Constants,
  LINEOFBUSINESS_TRANSLATION,
  LOB,
  MemberCoverageTypeToUICoverageTypeConversion,
} from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { mobileOnly } from '../../ConstantsStyles';
import { isCounty } from '../../SnackCardContainer/utils';
import {
  convertTypeaheadProviderIdAndType,
  getSearchLocation,
  getTypeAheadCategory,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { Breadcrumb } from '../Breadcrumb';
import { KeywordSearchDropdown } from './KeywordSearchDropdown';
import { LocationDropdown } from './LocationDropdown';
import { ChangeLocationForm } from './LocationDropdown/ChangeLocation';
import { ChangeLocationDrawer } from './LocationDropdown/LocationDropdown.styled';
import { LocationInput } from './LocationInput';
import { MobileSetLocation } from './MobileSetLocation';
import { DarkeningOverlay, InputBox, PositioningBox } from './SearchBar.styled';
import { SearchPlacement } from './SearchPlacement';
import {
  getGeoLocationFromStorage,
  getTypeAheadResultTrackingData,
  handleKeywordSuggestionKeyDownHelper,
  handleSearchBarSearchButtonClickOrEnter,
  setGeoLocationToStorage,
} from './utils';

const locationAlertId = 'location-instructions';

type Props = {
  choosePCPHeader?: boolean;
  memberLocation?: string;
  showSearchInputBackButton?: boolean;
  breadcrumbs?: any[];
  chipsToTabsEnabled: boolean;
  isMixedResultsPage: boolean;
  showResultsPageForMap: boolean;
  isSmartChoiceIconVisible?: boolean;
};

export const SearchBar = ({
  choosePCPHeader,
  memberLocation = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.MEMBER_LOCATION
  ) ?? '',
  showSearchInputBackButton = false,
  breadcrumbs = [],
  chipsToTabsEnabled,
  isMixedResultsPage,
  showResultsPageForMap,
  isSmartChoiceIconVisible = false,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const keywordSearchInputId = useId();
  const currentMember = getCurrentMember();
  const mobileScreen = useMediaQuery(mobileOnly);
  const data = useLagoon('ui-messaging')();
  const [, getLocation] = useLocation({});
  const isGuestUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );
  const isIFPEarlyPay = !!currentMember?.isIFPEarlyPay;

  const { navigate } = useRouter();
  const { setTypeaheadSearchStore, search, searchTerm, zipcode } =
    useTypeaheadStore(
      useShallow((state: TypeaheadState) => ({
        setTypeaheadSearchStore: state.setTypeaheadSearchStore,
        search: state.typeaheadSearchStore.search,
        searchTerm: state.typeaheadSearchStore.searchTerm,
        zipcode: state.typeaheadSearchStore.zipcode,
      }))
    );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setCoverageType, setChipValue } = chipStore;
  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState, choosePCP, dependentSeqNbr } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      choosePCP: state.pcpSearchState.choosePCP,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );
  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));

  const [isFocusedOnKeywordSearchInput, setIsFocusedOnKeywordSearchInput] =
    useState(false);
  const [isFocusedOnLocationSearch, setIsFocusedOnLocationSearch] =
    useState(false);
  const [isOpenMobileLocation, setIsOpenMobileLocation] = useState(false);
  const [isCurrentLocation, setIsCurrentLocation] = useState(true);
  const [keywordSearchTerm, setKeywordSearchterm] = useState(searchTerm || '');
  const { setIsCountySearch } = useContext(CountySearchContext);
  let searchText: string;
  const storedLocation = getGeoLocationFromStorage();
  const {
    id,
    name = '',
    latitude: geoLat,
    longitude: geoLong,
    zipCode,
    stateCode,
  } = storedLocation;
  const [locationInputVal, setLocationInputVal] = useState(name);
  const [isGeospatialOpen, setIsGeospatialOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [pendingLocation, setPendingLocation] = useState<
    GuestLocation | undefined
  >(undefined);
  const [postalPointResult, getPostalPoint] = useGetPostalPoint({});
  const [headers, setHeaders] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.TYPE_AHEAD_HEADERS,
    {}
  );
  const urlParams = new URLSearchParams(window.location.search);
  const [results, setResults] = useState<any>('');
  const [combineRollUpCodes, setCombineRollUpCodes] = useState<any>('');
  const [aggregatedAoeCodes, setAggregatedAoeCodes] = useState<string[]>([]);
  const [bhProgramFacilityAgg, setBhProgramFacilityAgg] = useState<any>('');

  const [enter, setEnter] = useState(false);

  const [locationSuggestions, setLocationSuggestions] = useState<object[]>();
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isKeywordSearchLoading, setIsKeywordSearchLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const clearTimerAndResetLoading = () => {
    setIsKeywordSearchLoading(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const [searchButtonResults, setSearchButtonResults] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.TYPE_AHEAD_SEARCH_RESULTS,
    []
  );
  const [autoCompleteCallCompleted, setAutoCompleteCallCompleted] =
    useState<any>(false);

  const [
    enableRetriveTypeAheadDataFlag,
    enableSearchResultsV2,
    isLLMGeneralSearchEnabled,
    enableTypeAheadWildCardSearch,
    hideDisabledTypeahead,
    enableNonEnglishCharacters,
    disableSearchBar,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RETRIVE_TYPEAHEAD_DATA,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LLM_TYPEAHEAD_GENERAL_SEARCH,
    currentMember?.isIFPEarlyPay
      ? ConstantsLagoon.FEATURE_FLAGS.ENABLE_TYPEAHEAD_WILDCARD_SEARCH_EARLYPAY
      : ConstantsLagoon.FEATURE_FLAGS.ENABLE_TYPEAHEAD_WILDCARD_SEARCH,
    FeatureFlags.FEATURE_FLAG_KEY.HIDE_DISABLED_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_ENGLISH_CHARACTERS_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_SEARCH_BAR,
  ]);

  const enableRetriveTypeAheadData =
    enableRetriveTypeAheadDataFlag && enableSearchResultsV2;

  const commonSearchesLagoon: () => any = useLagoon('common-searches');
  const commonSearchesData = commonSearchesLagoon();
  const memberCoverages = getCoverageTypes(currentMember);
  const convertedMemberCoverages = memberCoverages?.map(
    (cov) => MemberCoverageTypeToUICoverageTypeConversion[cov]
  );
  const dentalCoverage = convertedMemberCoverages?.find((cov) => cov === 'D');
  const visionCoverage = convertedMemberCoverages?.find((cov) => cov === 'V');
  const behavioralCoverage = convertedMemberCoverages?.find(
    (cov) => cov === 'B'
  );
  const medicalCoverage = convertedMemberCoverages?.find((cov) => cov === 'M');
  const getCoverageType = [
    dentalCoverage,
    visionCoverage,
    medicalCoverage,
    behavioralCoverage,
  ];
  const [captureResults] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_CAPTURE_RESULTS,
  ]);

  const [enableAutoCompleteTelemetry] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
  ]);

  const [enableUESSuggestionMatch] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_UES_SUGGESTION_MATCH,
  ]);

  const enableCountySelect =
    isGuestUser &&
    (currentMember?.lineOfBusiness === LOB.CNS ||
      currentMember?.lineOfBusiness === LOB.MNR);

  const specialityPreferenceTable = useLagoon(
    Constants.LAGOON_TABLE.SPECIALITY_PREFERENCE
  )()?.map((item) => ({
    rollupCode: item.rollupCode,
    specialityCode: item.specialityCode,
  }));

  const searchInputOptionLocation = 'search-input-option-list';

  const [cursor, setCursor] = useState(-1);
  const [, autoCompleteTelemetry] = useAutoCompleteTelemetry({});

  const searchLabelKey = isIFPEarlyPay
    ? ConstantsLagoon.SEARCH_LABEL_IFP_EARLYPAY
    : ConstantsLagoon.SEARCH_LABEL;
  const searchLabelText = find(data, {
    key: searchLabelKey,
  });

  const isShowingKeywordSearchDropdown = isFocusedOnKeywordSearchInput;
  const isShowingLocationDropdown = isFocusedOnLocationSearch && !mobileScreen;

  const setKeywordSearchInputFocused = () => {
    setIsFocusedOnKeywordSearchInput(true);
    setIsFocusedOnLocationSearch(false);
  };

  const setKeywordSearchInputBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsFocusedOnKeywordSearchInput(false);
  };

  const setLocationInputBlur = () => {
    setIsFocusedOnLocationSearch(false);
  };

  const handleKeywordSearchInputFocus = (value) => {
    setKeywordSearchInputFocused();
    setCursor(-1);
    setEnter(false);
    setResults([]);
    setAutoCompleteCallCompleted(false);
    handleKeywordSearchInputChange(value);
  };

  const handleCloseLocationDropdown = () => {
    setKeywordSearchInputBlur();
    setLocationInputBlur();
  };

  const handleCloseLocationDrawer = () => {
    setKeywordSearchInputBlur();
    setLocationInputBlur();
  };

  const setLocationInputFocused = () => {
    setIsFocusedOnLocationSearch(true);
    setIsFocusedOnKeywordSearchInput(false);
    setLocationSuggestions([]);
  };

  const handleLocationSearchInputFocus = () => {
    setTimeout(() => {
      setLocationInputFocused();
      setCursor(-1);
    }, 100);
  };

  const handleLocationSearchTextChange = (value: string) => {
    setCursor(-1);
    setIsFocusedOnLocationSearch(true);
    setLocationInputVal(value);
  };

  const handleBackNavigation = () => {
    const isDeepLink = urlParams.get('isDeepLink');
    if (isDeepLink === 'true') {
      navigate(-2);
    } else {
      navigate(-1);
    }
  };
  const [, SendEventCapture] = useEventCapture({
    onCompleted: (result) => result,
    onError: () => {},
  });
  const handleEventCapture = async (
    typeAheadSource,
    typeAheadFilterDataResponse,
    typeAheadOpperation,
    typeAheadCorrelationId,
    typeAheadRequestForEventCapture
  ) => {
    SendEventCapture({
      variables: {
        source: typeAheadSource,
        results: JSON.stringify(
          getTypeAheadResultTrackingData(typeAheadFilterDataResponse)
        ),
        operationName: typeAheadOpperation,
        correlationId: typeAheadCorrelationId,
        request: JSON.stringify(typeAheadRequestForEventCapture),
      },
    });
  };

  const hitAutoCompleteTelemetry = async (updatedParams) => {
    await autoCompleteTelemetry({
      variables: updatedParams,
    });
  };
  const typeaheadParams = useLagoon(Constants.LAGOON_TABLE.TYPEAHEAD_PARAMS)();
  const radius = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.RADIUS,
    Constants.TYPEAHEAD_PARAMS.RADIUS_DEFAULT
  );

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const commonTelemetryParamaters = {
    correlationId: correlationIDSessionStorage,
    timestamp: new Date().toISOString(),
    searchPlatform: 'web',
    searchLocation: {
      locationInputField: name || '',
      latitude: parseFloat(geoLat),
      longitude: parseFloat(geoLong),
      appliedSearchRadiusMiles: radius || 0,
    },
  };

  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [usedCorrelationID, setUsedCorrelationID] = useState('');

  const [typeAheadData, GetAutoCompleteResults] = useAutoCompleteResults({
    onCompleted: ({
      suggestions,
      typeAheadSuggestions,
      combinedRollupCodes,
      aggregatedAoeCodes,
      bhProgramFacilityAgg,
      requestData,
      correlationId,
      providers,
      facilities,
      filterData,
    }) => {
      clearTimerAndResetLoading();
      setSearchButtonResults(suggestions?.concat(providers).concat(facilities));
      setHeaders({
        correlationId,
      });
      correlationId && setCorrelationIDSessionStorage(correlationId);
      setCombineRollUpCodes(combinedRollupCodes);
      setAggregatedAoeCodes(aggregatedAoeCodes);
      setBhProgramFacilityAgg(bhProgramFacilityAgg);
      setResults(typeAheadSuggestions);
      setAutoCompleteCallCompleted(true);
      adobeImpressionTrackEvent({
        searchTerm: searchText,
        type: 'typeahead search',
        message:
          typeAheadSuggestions.length === 0
            ? 'no results found for your search'
            : 'results found for your search',
        customAttributesBlock: {
          correlationId,
          searchLocation: getSearchLocation(storedLocation),
        },
      });
      const telemetryNameSuggestions = [
        ...filterData?.practitioners_uhc?.provData?.map((item) => ({
          resultId: item?.providerId,
          resultType: getProviderType(item?.providerType),
          name: item?.displayName,
          specialty: item?.speciality?.map((s) => s?.category).join(', '),
          address: item?.displayAddress,
          coverageType: item?.coverageType?.map(getTelemetryCoverageType),
        })),
        ...filterData?.organizations_uhc?.orgData?.map((item) => ({
          resultId: item?.providerId,
          resultType: getProviderType(item?.providerType),
          name: item?.displayName,
          specialty: item?.speciality?.map((s) => s?.category).join(', '),
          address: item?.displayAddress,
          coverageType: item?.coverageType?.map(getTelemetryCoverageType),
        })),
      ];
      const telemetryNonNameSuggestions = [
        ...filterData?.lang_provider?.langProvider?.map((item) => ({
          label: item?.suggestion,
          coverage: item?.coverageType?.map(getTelemetryCoverageType),
        })),
      ];
      const updatedParams = {
        ...commonTelemetryParamaters,
        nameSuggestions: telemetryNameSuggestions,
        nonNameSuggestions: telemetryNonNameSuggestions,
        resultedInNullSearch: typeAheadSuggestions?.length === 0,
        interpretedAsNameSearch:
          telemetryNameSuggestions?.length > 0 &&
          telemetryNonNameSuggestions?.length === 0,
        searchQuery: storage.session.get(
          Constants.STORAGE_KEYS.SESSION.LAST_SEARCH_TERM
        ),
        correlationId,
        event: 'autocompleteSearch',
      };
      if (
        enableAutoCompleteTelemetry &&
        usedCorrelationID !== updatedParams?.correlationId
      ) {
        hitAutoCompleteTelemetry(updatedParams);
        setUsedCorrelationID(updatedParams?.correlationId);
      }

      if (captureResults) {
        handleEventCapture(
          'UES',
          filterData,
          'typeahead',
          correlationId,
          requestData?.variables
        );
      }
    },
    onError: () => {
      clearTimerAndResetLoading();
      setResults(null);
    },
  });

  const handleKeywordSearchInputChange = useCallback(
    debounce(async ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setKeywordSearchterm(value.trim());
      setEnter(false);
      if (value.trim()?.length > 0) {
        storage.session.set(
          Constants.STORAGE_KEYS.SESSION.LAST_SEARCH_TERM,
          value.trim()
        );
      }
      searchText = value.trim();
      setCursor(-1);
      setResults([]);
      clearTimerAndResetLoading();
      if (
        value.replace(/\s/g, '').length > 1 &&
        (enableNonEnglishCharacters ? true : /[A-Za-z0-9]/.test(value))
      ) {
        const normalizedValue = value
          .trim()
          .replace(/&/g, ' and ')
          .replace(/"/g, '');
        GetAutoCompleteResults({
          query: encodeURIComponent(normalizedValue),
        });
        if (typeAheadData?.isLoading) {
          timerRef.current = setTimeout(() => {
            setIsKeywordSearchLoading(true);
          }, 5000);
        }
      }
    }, 400),
    [geoLat, geoLong, currentMember, typeAheadData]
  );

  const checkForEmptyLocation = (): void => {
    if (!locationInputVal) {
      const storedLocation = getGeoLocationFromStorage();
      const { name } = storedLocation;
      setLocationInputVal(name);
    }
  };

  const isShowingLocationDrawer = useMemo(
    () => isFocusedOnLocationSearch && mobileScreen,
    [isFocusedOnLocationSearch, mobileScreen]
  );

  const getTypeaheadData = () =>
    cursor === -1 ? searchButtonResults?.[0] : searchButtonResults?.[cursor];

  const [searchParams] = useSearchParams();
  const {
    search: searchFromParams,
    isSearchButtonClickOrKeyboardEnter,
    isSuggestionClickOrEnter,
  } = getSearchInfo(searchParams);

  const handleKeywordSuggestionKeyDown = (ev: {
    key: string;
    preventDefault: () => void;
    target: { value: string };
  }) => {
    const allowEnter = enableTypeAheadWildCardSearch || cursor !== -1;
    if (ev.key === 'Enter' && allowEnter && !isKeywordSearchLoading) {
      const suggestionsArray = searchButtonResults.filter(
        (item) => item.suggestion !== undefined
      );
      const providersArray = searchButtonResults.filter(
        (item) => item.locationId !== undefined
      );

      const telemetryNameSuggestions = [
        ...providersArray?.map((item) => ({
          resultId: item?.providerId,
          resultType: getProviderType(item?.providerType),
          name: item?.displayName,
          specialty: item?.speciality?.map((s) => s?.category).join(', '),
          address: item?.displayAddress,
          coverageType: item?.coverageType?.map(getTelemetryCoverageType),
        })),
      ];
      const telemetryNonNameSuggestions = [
        ...suggestionsArray?.map((item) => ({
          label: item?.suggestion,
          coverage: item?.coverageType?.map(getTelemetryCoverageType),
        })),
      ];
      const updatedParams = {
        ...commonTelemetryParamaters,
        nameSuggestions: telemetryNameSuggestions,
        nonNameSuggestions: telemetryNonNameSuggestions,
        resultedInNullSearch: searchButtonResults?.length === 0,
        searchQuery: keywordSearchTerm,
        searchError: searchParams?.get('code') || '',
        correlationId: correlationIDSessionStorage,
        event: 'generalSearch',
      };
      if (enableAutoCompleteTelemetry) {
        hitAutoCompleteTelemetry(updatedParams);
      }
    }
    handleKeywordSuggestionKeyDownHelper(
      ev,
      specialityPreferenceTable,
      enableTypeAheadWildCardSearch,
      enableRetriveTypeAheadData,
      {
        keywordSearchTerm,
        commonSearchesData,
        isKeywordSearchLoading,
        searchButtonResults,
        setEnter,
        cursor,
        results,
        combineRollUpCodes,
        aggregatedAoeCodes,
        bhProgramFacilityAgg,
        store: {
          chipStore,
          setTypeaheadSearchStore,
          setAnalyticsState,
          setPCPSearchState,
          setChipValue,
          setCoverageType,
          setDetailsStore,
          setSearchStore,
        },
        navigate,
        getTypeaheadData,
        headers,
        setCursor,
        convertTypeaheadProviderIdAndType,
        setIsFocusedOnKeywordSearchInput,
        searchInputOptionLocation,
        blurSearchInput,
        choosePCP,
        dependentSeqNbr,
        getCoverageType,
        enableUESSuggestionMatch,
        currentMember,
        searchFromParams,
        isSuggestionClickOrEnter,
        isSearchButtonClickOrKeyboardEnter,
      }
    );
  };

  const searchInputRef = useRef<HTMLInputElement>(null);
  const blurSearchInput = () => {
    searchInputRef?.current?.blur();
  };

  const onZipcodeChangeFromQueryParam = async (zipCode?: string) => {
    const { data } = await getLocation({
      variables: {
        address: zipCode,
        countySearchEnabled: true,
      },
    });
    if (data) {
      const locationData = data.location.features[0];
      const [selectedLong, selectedLat] = locationData.center || [];

      setIsCountySearch(isCounty(id));

      setGeoLocationToStorage({
        id: locationData.id,
        name: locationData.place_name,
        longitude: selectedLong,
        latitude: selectedLat,
        stateCode: locationData.stateCode,
        zipCode: locationData.zipCode,
      });
    }
  };

  const handleGeneralSearchClick = () => {
    const analyticsLinkName = 'general search';
    const typeaheadLinkName = 'typeahead general search';
    setShowMaximumProviderLimitBanner(false);
    handleSearchBarSearchButtonClickOrEnter(
      {
        isLLMGeneralSearchEnabled,
        enableUESSuggestionMatch,
        enableRetriveTypeAheadData,
        typeAheadSuggestions: results || [],
        navigate,
        headers,
        analyticsLinkName,
        typeaheadLinkName,
        currentMember,
        userSearchTerm: keywordSearchTerm,
        combinedRollupCodes: combineRollUpCodes,
        aggregatedAoeCodes,
        bhProgramFacilityAgg,
        setIsFocusedOnKeywordSearchInput,
        convertTypeaheadProviderIdAndType,
        getTypeAheadCategory,
        store: {
          chipStore,
          setTypeaheadSearchStore,
          setAnalyticsState,
          setPCPSearchState,
          setChipValue,
          setCoverageType,
          setDetailsStore,
        },
      },
      specialityPreferenceTable
    );
  };

  useEffect(() => {
    if (searchParams?.get('source') === 'AutoComplete') {
      const updatedParams = {
        ...commonTelemetryParamaters,
        searchQuery: storage.session.get(
          Constants.STORAGE_KEYS.SESSION.LAST_SEARCH_TERM
        ),
        searchError: searchParams?.get('code') || '',
        event: 'autocompleteSearch',
      };
      if (enableAutoCompleteTelemetry) {
        hitAutoCompleteTelemetry(updatedParams);
      }
    }
  }, [keywordSearchTerm]);

  useEffect(() => {
    if (isCounty(id)) setIsCountySearch(isCounty(id));
  }, [id, setIsCountySearch]);

  useEffect(() => {
    !isFocusedOnLocationSearch && checkForEmptyLocation();
  }, [isFocusedOnLocationSearch]);

  useEffect(() => {
    if (searchInputRef.current && !isGuestUser) {
      searchInputRef.current.value = '';
    }
  }, [JSON.stringify(currentMember)]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a modal overlay or modal content
      const isModalClick = event.target.closest('.abyss-modal-overlay');

      const isOutsideInput = wrapperRef.current
        ? !wrapperRef.current.contains(event.target)
        : true;
      if (isOutsideInput && !isShowingLocationDrawer && !isModalClick) {
        setKeywordSearchInputBlur();
        setLocationInputBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keyup', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keyup', handleClickOutside);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [wrapperRef, isShowingLocationDrawer]);

  useEffect(() => {
    if (zipcode) onZipcodeChangeFromQueryParam(zipcode);
  }, [zipcode]);

  useEffect(() => {
    if (
      (!typeAheadData?.loading || !!typeAheadData?.error) &&
      timerRef.current
    ) {
      clearTimerAndResetLoading();
    }
  }, [typeAheadData?.loading, !!typeAheadData?.error]);

  const handlePostalLocationSelected = async (data: PostalLocationData) => {
    const { selectedLocation, variables } = data;
    try {
      const postalPointResponse = await getPostalPoint({ variables });
      const postalPointData = postalPointResponse?.data?.getPostalPoint;
      const postalPoint = Array.isArray(postalPointData)
        ? postalPointData?.[0]
        : postalPointData;
      const multicountyFlag = String(
        postalPoint?.multicounty_flag || ''
      ).trim();
      if (multicountyFlag === 'Y' && postalPoint?.us_postal_point_id) {
        setPendingLocation({
          ...selectedLocation,
          stateCode: postalPoint?.state || selectedLocation.stateCode,
          countyId: postalPoint?.county_id || undefined,
        });
        setSelectedCounty('');
        setIsGeospatialOpen(true);
        return;
      }
      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
      setGeoLocationToStorage({
        name: selectedLocation.label,
        longitude: selectedLocation.longitude,
        latitude: selectedLocation.latitude,
        stateCode: postalPoint?.state || selectedLocation.stateCode,
        zipCode: selectedLocation.zipcode || '',
        county: postalPoint?.county_id,
      });
    } catch {
      setGeoLocationToStorage({
        name: selectedLocation.label,
        longitude: selectedLocation.longitude,
        latitude: selectedLocation.latitude,
        stateCode: selectedLocation.stateCode,
        zipCode: selectedLocation.zipcode || '',
      });
      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
    }
  };

  const handleGeospatialSearch = () => {
    if (!selectedCounty || !pendingLocation) return;
    setIsGeospatialOpen(false);
    const [stateCode, countyId] = selectedCounty.split('-');
    const newLocation = { ...pendingLocation, stateCode, countyId };
    setGeoLocationToStorage({
      name: newLocation.label,
      longitude: newLocation.longitude,
      latitude: newLocation.latitude,
      stateCode: newLocation.stateCode,
      zipCode: newLocation.zipcode || '',
      county: newLocation.countyId,
    });
    sessionStorage.setItem('countyFipsCode', countyId);
    sessionStorage.setItem(
      Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED,
      'true'
    );
  };

  const usPostalPointId = useMemo(() => {
    const postalPointData = postalPointResult?.data?.getPostalPoint;
    const postalPoint = Array.isArray(postalPointData)
      ? postalPointData?.[0]
      : postalPointData;
    return postalPoint?.us_postal_point_id || '';
  }, [postalPointResult?.data?.getPostalPoint]);

  return (
    <React.Fragment>
      {enableCountySelect && (
        <GeospatialModal
          isOpen={isGeospatialOpen}
          lob={LINEOFBUSINESS_TRANSLATION[currentMember?.lineOfBusiness]}
          onClose={() => setIsGeospatialOpen(false)}
          onSearch={handleGeospatialSearch}
          selectedCounty={selectedCounty}
          setSelectedCounty={setSelectedCounty}
          usPostalPointId={usPostalPointId}
        />
      )}
      {mobileScreen && isShowingKeywordSearchDropdown && <DarkeningOverlay />}
      <PositioningBox
        css={{
          height: chipsToTabsEnabled && !isMixedResultsPage ? '40px' : '62px',
          marginTop:
            mobileScreen && isSmartChoiceIconVisible && !choosePCPHeader
              ? '40px'
              : '0px',
        }}
        ref={wrapperRef}
      >
        {mobileScreen && showSearchInputBackButton ? (
          <Breadcrumb
            breadcrumbs={breadcrumbs}
            onBackButtonClick={handleBackNavigation}
            showBreadcrumbAtMediumScreenSize={
              showResultsPageForMap ? false : true
            }
          />
        ) : null}
        {!disableSearchBar ? (
          <InputBox
            hasShadow={isShowingKeywordSearchDropdown}
            isSharpBottomRightCorner={isShowingLocationDropdown}
            mobileScreen={mobileScreen}
          >
            <SearchPlacement
              choosePCPHeader={choosePCPHeader}
              commonSearchesData={commonSearchesData}
              dentalCoverage={dentalCoverage}
              handleKeywordSearchInputChange={handleKeywordSearchInputChange}
              handleKeywordSearchInputFocus={handleKeywordSearchInputFocus}
              handleKeywordSuggestionKeyDown={handleKeywordSuggestionKeyDown}
              isFocusedOnKeywordSearchInput={isFocusedOnKeywordSearchInput}
              isFocusedOnLocationSearch={isFocusedOnLocationSearch}
              keywordSearchInputId={keywordSearchInputId}
              keywordSearchTerm={keywordSearchTerm}
              search={search}
              searchButtonResults={searchButtonResults}
              searchInputRef={searchInputRef}
              searchLabelText={searchLabelText}
              setResults={setResults}
              setSearchButtonResults={setSearchButtonResults}
              visionCoverage={visionCoverage}
            />

            {isShowingKeywordSearchDropdown && (
              <KeywordSearchDropdown
                activeSuggestion={cursor}
                autoCompleteCallCompleted={autoCompleteCallCompleted}
                blurKeywordSearchInput={blurSearchInput}
                checkForEmptyLocation={checkForEmptyLocation}
                dentalCoverage={dentalCoverage}
                enableUESSuggestionMatch={enableUESSuggestionMatch}
                enter={enter}
                getCoverageType={getCoverageType}
                handleGeneralSearchClick={handleGeneralSearchClick}
                headers={headers}
                hideDisabledTypeahead={hideDisabledTypeahead}
                isKeywordSearchLoading={isKeywordSearchLoading}
                isLLMGeneralSearchEnabled={isLLMGeneralSearchEnabled}
                isLoading={typeAheadData?.isLoading}
                keywordSearchTerm={keywordSearchTerm}
                medicalCoverage={medicalCoverage}
                memberDDP={currentMember?.ddpCode}
                search={searchInputRef.current?.value || ''}
                setIsFocusedOnKeywordSearchInput={
                  setIsFocusedOnKeywordSearchInput
                }
                specialityRollUpCodes={combineRollUpCodes}
                typeAheadSuggestions={results}
                visionCoverage={visionCoverage}
              />
            )}
          </InputBox>
        ) : null}

        <LocationInput
          aggregatedAoeCodes={aggregatedAoeCodes}
          bhProgramFacilityAgg={bhProgramFacilityAgg}
          checkForEmptyLocation={checkForEmptyLocation}
          combinedRollupCodes={combineRollUpCodes}
          cursor={cursor}
          data-testid="location-search-input"
          enableCountySelect={enableCountySelect}
          enableTypeAheadWildCardSearch={enableTypeAheadWildCardSearch}
          enableUESSuggestionMatch={enableUESSuggestionMatch}
          handleCloseLocationDrawer={handleCloseLocationDrawer}
          handleCloseLocationDropdown={handleCloseLocationDropdown}
          headers={headers}
          id={locationAlertId}
          isFocusedOnKeywordSearchInput={isFocusedOnKeywordSearchInput}
          isFocusedOnLocationSearch={isFocusedOnLocationSearch}
          isOpenMobileLocation={isOpenMobileLocation}
          isShowingLocationDrawer={isShowingLocationDrawer}
          isShowingLocationDropdown={isShowingLocationDropdown}
          lob={currentMember?.lineOfBusiness}
          locationValue={locationInputVal}
          memberLocation={memberLocation}
          onLocationSearchInputFocus={handleLocationSearchInputFocus}
          onLocationSearchInputTextChange={handleLocationSearchTextChange}
          onPostalLocationSelected={handlePostalLocationSelected}
          placeHolderValue={name}
          searchTerm={keywordSearchTerm}
          setCursor={setCursor}
          setIsCurrentLocation={setIsCurrentLocation}
          setIsFocusedOnKeywordSearchInput={setIsFocusedOnKeywordSearchInput}
          setIsLocationLoading={setIsLocationLoading}
          setIsOpenMobileLocation={setIsOpenMobileLocation}
          setLocationInputVal={setLocationInputVal}
          setLocationSuggestions={setLocationSuggestions}
          specialityPreferenceTable={specialityPreferenceTable}
          typeAheadSuggestions={results}
        />

        {isShowingLocationDropdown && !isShowingKeywordSearchDropdown && (
          <LocationDropdown
            activeSuggestion={cursor}
            data-testid="location-search-bar-dropdown"
            id={locationAlertId}
            isCurrentLocation={isCurrentLocation}
            isLocationLoading={isLocationLoading}
            locationInputVal={locationInputVal}
            locationSuggestions={locationSuggestions}
            onClose={handleCloseLocationDropdown}
            setIsCurrentLocation={setIsCurrentLocation}
            setLocationInputVal={setLocationInputVal}
            setLocationSuggestions={setLocationSuggestions}
            setSelectedLocation={(targetLocation) => {
              const {
                id: locationId,
                center = [],
                place_name: placeName,
                stateCode: targetStateCode,
                zipCode: targetZipCode,
              } = targetLocation;
              const [selectedLong, selectedLat] = center;
              setIsCountySearch(isCounty(locationId));

              if (isGuestUser) {
                const isPostcode = locationId?.includes('postcode');
                const isPlace = locationId?.includes('place');

                const isPlacecodeSelection =
                  currentMember?.lineOfBusiness === LOB.MNR && isPlace;
                const shouldUsePostalPointApi =
                  isPostcode || isPlacecodeSelection;
                if (shouldUsePostalPointApi && enableCountySelect) {
                  const isPostcode = locationId?.includes('postcode');
                  const isPlace = locationId?.includes('place');
                  if (isPostcode || isPlace) {
                    const guestLocation: GuestLocation = {
                      label: placeName,
                      latitude: String(selectedLat),
                      longitude: String(selectedLong),
                      zipcode: targetZipCode || '',
                      stateCode: targetStateCode,
                    };
                    const variables = isPostcode
                      ? { postal: targetZipCode }
                      : {
                          city: String(placeName.split(',')[0])
                            .toUpperCase()
                            .trim(),
                          state:
                            String(targetStateCode)?.toUpperCase().trim() || '',
                        };
                    handlePostalLocationSelected({
                      selectedLocation: guestLocation,
                      variables,
                    });
                    return;
                  }
                }
              }
              setGeoLocationToStorage({
                id: locationId,
                name: placeName,
                longitude: selectedLong,
                latitude: selectedLat,
                stateCode: targetStateCode,
                zipCode: targetZipCode,
              });
            }}
          />
        )}

        <ChangeLocationDrawer
          isOpen={isShowingLocationDrawer}
          onClose={handleCloseLocationDrawer}
          position="bottom"
          size="$lg"
          title={t('Change Location')}
        >
          <ChangeLocationForm
            data-testid="change-location-form"
            onLocationSelect={handleCloseLocationDrawer}
          />
        </ChangeLocationDrawer>
      </PositioningBox>
      {mobileScreen && (
        <MobileSetLocation
          locationInputVal={locationInputVal}
          setIsOpenMobileLocation={setIsOpenMobileLocation}
          stateCode={stateCode}
          zipCode={zipCode}
        />
      )}
    </React.Fragment>
  );
};
